import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import PatientPaymentLinkCard from "@/components/payments/PatientPaymentLinkCard";

// =====================================================================
// /pay/:token — public, anonymous payment page.
//
// What the patient sees:
//   * Clinic name + amount
//   * Big QR (downloadable)
//   * Upload-proof button (file picker only; patient links should not ask for camera capture)
//   * After submission: "✓ Sent. Waiting for the clinic to verify."
//
// No reference-number input — staff verifies the screenshot. No PII
// shown beyond clinic name + amount. Token is the bearer credential;
// without it, none of the RPCs return anything.
// =====================================================================

const PROOFS_BUCKET = "payment-proofs";

export default function Pay() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [link, setLink]       = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    const { data, error: e } = await supabase.rpc("get_payment_link_by_token", { p_token: token });
    if (!silent) setLoading(false);
    if (e) {
      if (!silent) setError(e.message ?? "Could not load this payment link.");
      return;
    }
    setLink(data);
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!link || ["verified", "expired", "cancelled"].includes(link.status)) return undefined;
    const timer = setInterval(() => {
      load({ silent: true });
    }, 4000);
    return () => clearInterval(timer);
  }, [link, load]);

  function handlePickFile() {
    fileRef.current?.click();
  }

  async function handleFileChosen(e) {
    const file = e.target?.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setUploadError("Please upload an image of your payment receipt.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("File too large (max 5 MB).");
      return;
    }
    setUploadError(null);
    setUploading(true);
    const ext = (file.name.split(".").pop() || "png").toLowerCase();
    const path = `${token}/${crypto.randomUUID()}.${ext}`;
    const { error: e1 } = await supabase.storage
      .from(PROOFS_BUCKET)
      .upload(path, file, { cacheControl: "3600", upsert: false });
    if (e1) {
      setUploading(false);
      setUploadError(e1.message ?? "Upload failed.");
      return;
    }
    const { data: urlData } = supabase.storage.from(PROOFS_BUCKET).getPublicUrl(path);
    const proofUrl = urlData?.publicUrl;
    if (!proofUrl) {
      setUploading(false);
      setUploadError("Couldn't get the public URL for the upload.");
      return;
    }
    const { error: e2 } = await supabase.rpc("attach_payment_proof", {
      p_token: token, p_proof_url: proofUrl,
    });
    setUploading(false);
    if (e2) {
      setUploadError(e2.message ?? "We couldn't attach the proof. Please try again.");
      return;
    }
    load();
  }

  function handleDownloadQR() {
    if (!link?.qr_image_url) return;
    const a = document.createElement("a");
    a.href = link.qr_image_url;
    a.download = `${(link.clinic_name || "clinic").replace(/[^A-Za-z0-9]+/g, "-")}-QR.png`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  }

  // ----- States --------------------------------------------------------

  if (loading) {
    return (
      <Shell>
        <div className="text-center text-sm text-slate-400 py-12">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…
        </div>
      </Shell>
    );
  }
  if (error || !link) {
    return (
      <Shell>
        <div className="text-center py-8">
          <AlertTriangle className="w-8 h-8 mx-auto text-rose-400" />
          <h1 className="text-base font-semibold text-slate-900 mt-3">This link isn't valid</h1>
          <p className="text-sm text-slate-500 mt-1">{error || "Link not found."}</p>
        </div>
      </Shell>
    );
  }
  if (link.status === "expired" || link.status === "cancelled") {
    return (
      <Shell>
        <div className="text-center py-8">
          <Clock className="w-8 h-8 mx-auto text-slate-400" />
          <h1 className="text-base font-semibold text-slate-900 mt-3">This payment link is no longer active</h1>
          <p className="text-sm text-slate-500 mt-1">Please contact the clinic for a fresh link.</p>
        </div>
      </Shell>
    );
  }
  if (link.status === "verified") {
    return (
      <Shell>
        <div className="text-center py-8">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
          <h1 className="text-lg font-semibold text-slate-900 mt-3">Payment complete</h1>
          <p className="text-sm text-slate-500 mt-1">Thanks. The clinic has verified your payment.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <PatientPaymentLinkCard
        link={link}
        uploading={uploading}
        uploadError={uploadError}
        fileRef={fileRef}
        onDownloadQR={handleDownloadQR}
        onPickFile={handlePickFile}
        onFileChosen={handleFileChosen}
      />
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center py-10 px-4">
      {children}
    </div>
  );
}
